import { Component, Input, OnChanges, SimpleChanges, signal } from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { ImagePreloaderDirective } from '../../directives/image-preloader.directive';
import { InitialsFromNamePipe } from '../../pipes/initials-from-name.pipe';

const DEFAULT_SIZE = '40px';

@Component({
  selector: 'app-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  imports: [NgClass, NgStyle, ImagePreloaderDirective, InitialsFromNamePipe],
})
export class AvatarComponent implements OnChanges {
  /** A picture to show. For users this is the presigned `profilePictureUrl`; for sales channels, a logo. */
  @Input() src?: string;
  /** Present for people. Supplies the initials shown when there is no usable picture. */
  @Input() fullName?: string;
  @Input() width: string;
  @Input() height: string;
  @Input() alt?: string;
  @Input() type: 'square' | 'circle' = 'circle';

  imgWidth: string = DEFAULT_SIZE;
  imgHeight: string = DEFAULT_SIZE;

  /** CRAT-1314 — set when a user's picture fails to load, e.g. an expired presigned url. */
  readonly imageFailed = signal(false);

  fontSize() {
    return parseInt(this.height) / 2 + 'px';
  }

  /**
   * A person whose picture will not load falls back to their initials, which reads as intended
   * rather than as a broken image. A channel logo has no initials to fall back to, so it keeps the
   * preloader directive's own placeholder behaviour.
   */
  showPicture(): boolean {
    return !!this.src && !(this.imageFailed() && !!this.fullName);
  }

  onImageError(): void {
    this.imageFailed.set(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A newly uploaded picture deserves a fresh attempt, even if the previous url had failed.
    if (changes['src'] && !changes['src'].isFirstChange()) {
      this.imageFailed.set(false);
    }

    if (!this.width && !this.height) {
      this.imgWidth = DEFAULT_SIZE;
      this.imgHeight = DEFAULT_SIZE;
    }

    if (this.width && !this.height) {
      this.height = this.width;
    }

    if (this.height && !this.width) {
      this.width = this.height;
    }

    if (this.width && /^\d+$/.test(this.width)) {
      this.imgWidth = this.width + 'px';
    }

    if (this.height && /^\d+$/.test(this.height)) {
      this.imgHeight = this.height + 'px';
    }
  }
}
